import { z } from 'zod';
import { decimalSchema } from './prisma-zod';
import { CrmStageTypeEnum } from './crm-stage';
import { DeliverableBillingTypeSchema } from './deliverable';
import { RecordStateSchema } from './merge';
import { CaptureRefSchema } from './capture';

// Deal sort field enum
export enum DealSortFieldEnum {
  NAME = 'name',
  DESCRIPTION = 'description',
  STAGE = 'stage',
  STAGE_TYPE = 'stageTypeId',
  AMOUNT = 'amount',
  PROBABILITY = 'probability',
  DERIVED_PROBABILITY = 'derivedProbability',
  // T7 — rep-vs-derived gap, "the most useful number in the pipeline" (Lever 2).
  DERIVED_PROBABILITY_DELTA = 'derivedProbabilityDelta',
  HEALTH_SCORE = 'healthScore',
  HEALTH_LEVEL = 'healthLevel',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  HEALTH_CALCULATED_AT = 'healthCalculatedAt',
}

/**
 * T7 — derived-vs-rep delta (Lever 2).
 *
 * The gap between a rep's manual confidence (`probability`) and the tenant's own
 * historical win-rate for the deal's canonical stage (`derivedProbability`, T6)
 * is the forecast signal: a positive delta means the rep is more optimistic than
 * history ("they know something the base rate can't see" — or happy-ears), a
 * negative one means more pessimistic (honesty about a dying deal, or
 * sandbagging). The number only exists when BOTH inputs do.
 */

/**
 * Absolute gap at/above which a deal is flagged as a large divergence — the
 * deals "a manager's Monday is for". Kept here so backend filtering and frontend
 * highlighting share one threshold.
 */
export const DERIVED_PROBABILITY_DIVERGENCE_THRESHOLD = 30

/**
 * delta = rep confidence − derived probability.
 *
 * Returns null unless BOTH numbers exist — a cold-start deal (derived withheld
 * below the T9 sample floor) or one with no rep confidence has no gap to show,
 * and a null sorts/filters distinctly from a real 0 gap (genuine agreement).
 */
export function computeDerivedProbabilityDelta(
  repConfidence: number | null | undefined,
  derivedProbability: number | null | undefined,
): number | null {
  if (repConfidence == null || derivedProbability == null) return null
  return repConfidence - derivedProbability
}

/** True when a delta is large enough to warrant a manager's attention. */
export function isLargeDivergence(
  delta: number | null | undefined,
  threshold: number = DERIVED_PROBABILITY_DIVERGENCE_THRESHOLD,
): boolean {
  return delta != null && Math.abs(delta) >= threshold
}

/**
 * T9 (D4) — cold-start honesty. A deal is "cold-start" when its derived
 * win-probability is withheld (`derivedProbability == null`) DESPITE the
 * recompute having processed it — which it signals by writing a sample size N
 * (`derivedProbabilitySampleSize != null`) for every OPEN deal it scans. Closed
 * /settled deals are never scanned, so their sample size stays null; that's what
 * separates an honest cold-start ("not enough history at this stage yet, leaning
 * on rep confidence") from a deal that simply has no base rate because it's done.
 *
 * Derived (not persisted) from the two fields the recompute already writes, so
 * no extra column is needed.
 */
export function isDerivedProbabilityColdStart(
  derivedProbability: number | null | undefined,
  sampleSize: number | null | undefined,
): boolean {
  return derivedProbability == null && sampleSize != null
}

/**
 * T8 — append-only rep-confidence change history (Lever 2).
 *
 * Once confidence affects a forecast reps are judged on, it becomes a
 * negotiating position — "you were at 80% for two months on a deal that never
 * moved" is a coachable conversation. Each entry records one manual
 * `Deal.probability` change plus a snapshot of what the derived base rate (T6)
 * said AT THE TIME, so a review compares the rep's read against history at
 * that moment (the live derived number keeps moving with the funnel).
 *
 * Deliberately surface-less (ui-surfaces.md): at most a sparkline/history
 * popover on the deal — never a page.
 */
export const DealProbabilityHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  dealId: z.string().uuid(),
  /** Rep confidence before the change — null for the initial set at creation. */
  previousProbability: z.number().nullable(),
  /** Rep confidence after the change — null when the rep cleared their read. */
  newProbability: z.number().nullable(),
  /** Deal.derivedProbability at the moment of the change (null = withheld). */
  derivedProbabilityAtChange: z.number().nullable(),
  /** Canonical stage category (CrmStageTypeEnum) at the change. */
  stageTypeId: z.string().nullable(),
  /** Who changed it — null for system/workflow/import writers. */
  changedById: z.string().uuid().nullable(),
  changedBy: z
    .object({
      id: z.string().uuid(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.date(),
});

export type DealProbabilityHistoryEntry = z.infer<
  typeof DealProbabilityHistoryEntrySchema
>;

/** Default page size for GET /deals/:id/probability-history (newest-first). */
export const DEAL_PROBABILITY_HISTORY_DEFAULT_LIMIT = 50;
/** Hard cap on a single probability-history read. */
export const DEAL_PROBABILITY_HISTORY_MAX_LIMIT = 200;

/**
 * T11 (A2/A3) — deal-shape metadata. "Forecast work shape, not money": a
 * licensing-heavy deal needs almost nobody, a pure-services deal of the same
 * value is entirely people. The deal type captures that shape so demand can be
 * seeded from a pipeline deal (T14) rather than authored by hand.
 */
export const DealTypeEnum = {
  /** People-heavy delivery — value is almost entirely labour. */
  SERVICES: 'SERVICES',
  /** Ongoing retained services — recurring effort each period. */
  RETAINER: 'RETAINER',
  /** Software / license-heavy — high value, low internal effort. */
  LICENSING: 'LICENSING',
  /** Recurring product / subscription revenue. */
  SUBSCRIPTION: 'SUBSCRIPTION',
  /** One-off product sale. */
  PRODUCT: 'PRODUCT',
  /** A blend of the above. */
  MIXED: 'MIXED',
} as const;

export type DealType = (typeof DealTypeEnum)[keyof typeof DealTypeEnum];

export const DealTypeSchema = z.enum([
  DealTypeEnum.SERVICES,
  DealTypeEnum.RETAINER,
  DealTypeEnum.LICENSING,
  DealTypeEnum.SUBSCRIPTION,
  DealTypeEnum.PRODUCT,
  DealTypeEnum.MIXED,
]);

/**
 * Rough size band for a deliverable-mix row. Coarse on purpose — the rep gives a
 * t-shirt size, the system (T12 effort templates) expands it into role-hours.
 * "Never push role-hour estimation onto the rep."
 */
export const DealDeliverableSizeEnum = {
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
} as const;

export type DealDeliverableSize =
  (typeof DealDeliverableSizeEnum)[keyof typeof DealDeliverableSizeEnum];

export const DealDeliverableSizeSchema = z.enum([
  DealDeliverableSizeEnum.SMALL,
  DealDeliverableSizeEnum.MEDIUM,
  DealDeliverableSizeEnum.LARGE,
]);

/**
 * One row of a deal's rough deliverable mix. References a deliverable *type* by
 * its billing-type vocabulary (T23/T24 `DeliverableBillingType`) plus a coarse
 * size band and a count. Deliberately light: the deal carries type + rough size,
 * and role-hours are expanded later by T12 templates — they are never entered
 * here. `billingType` is validated against the deliverable vocabulary so a mix
 * can only reference a real deliverable type.
 */
export const DealDeliverableMixItemSchema = z.object({
  billingType: DeliverableBillingTypeSchema,
  size: DealDeliverableSizeSchema.default(DealDeliverableSizeEnum.MEDIUM),
  quantity: z.number().int().positive().max(999).default(1),
  label: z.string().trim().max(255).optional(),
});

export type DealDeliverableMixItem = z.infer<typeof DealDeliverableMixItemSchema>;

export const DealDeliverableMixSchema = z
  .array(DealDeliverableMixItemSchema)
  .max(50);

export type DealDeliverableMix = z.infer<typeof DealDeliverableMixSchema>;

/**
 * Validate + normalize a raw deliverable-mix payload, throwing on bad
 * deliverable-type references (an unknown `billingType`) or malformed rows.
 * Returns an empty array for null/undefined so the column always holds a valid
 * array. Used by the backend before persisting — the deal controller has no
 * zod pipe, so validation lives here at the service boundary.
 */
export function normalizeDealDeliverableMix(raw: unknown): DealDeliverableMix {
  if (raw == null) return [];
  return DealDeliverableMixSchema.parse(raw);
}

/**
 * Forecast category — the rep's call on how a deal counts toward the period
 * number (Clari-style). Orthogonal to pipeline stage: a negotiation-stage deal
 * can be a firm 'commit' or merely 'best_case'. 'omitted' keeps a deal open
 * but out of the roll-up.
 */
export enum DealForecastCategoryEnum {
  PIPELINE = 'pipeline',
  BEST_CASE = 'best_case',
  COMMIT = 'commit',
  OMITTED = 'omitted',
}

export const DealForecastCategorySchema = z.nativeEnum(DealForecastCategoryEnum);

export const DEAL_FORECAST_CATEGORY_LABELS: Record<DealForecastCategoryEnum, string> = {
  [DealForecastCategoryEnum.PIPELINE]: 'Pipeline',
  [DealForecastCategoryEnum.BEST_CASE]: 'Best case',
  [DealForecastCategoryEnum.COMMIT]: 'Commit',
  [DealForecastCategoryEnum.OMITTED]: 'Omitted',
};

/** Per-category totals in a forecast roll-up. Amounts in org currency. */
export interface DealForecastCategoryRollup {
  count: number;
  amount: number;
  /** Sum of amount × rep confidence (probability/100); deals without rep confidence contribute 0. */
  repWeighted: number;
  /** Sum of amount × derived probability; cold-start deals contribute 0. */
  derivedWeighted: number;
}

export interface DealForecastDivergentDeal {
  id: string;
  name: string;
  amount: number;
  stage: string;
  forecastCategory: DealForecastCategoryEnum;
  probability: number | null;
  derivedProbability: number | null;
  derivedProbabilityDelta: number | null;
  closeDate: string | null;
}

/** Response of GET /deals/forecast — the period roll-up. */
export interface DealForecastRollup {
  periodStart: string;
  periodEnd: string;
  categories: Record<DealForecastCategoryEnum, DealForecastCategoryRollup>;
  /** Deals already WON with closeDate inside the period. */
  wonAmount: number;
  wonCount: number;
  /** Open deals with no closeDate — outside the roll-up, shown for hygiene. */
  unscheduledCount: number;
  unscheduledAmount: number;
  /** Open deals in-period where |rep − derived| ≥ divergence threshold. */
  divergentDeals: DealForecastDivergentDeal[];
}

/** One deal's movement between a snapshot baseline and now. */
export interface DealMovementItem {
  id: string;
  name: string;
  amount: number;
  stage: string;
  stageTypeId: string | null;
  forecastCategory: string | null;
  closeDate: string | null;
  /** Baseline values (from the snapshot) where the movement is a diff. */
  previousCloseDate?: string | null;
  previousAmount?: number;
  previousStage?: string | null;
  /** amount − previousAmount for increased/decreased buckets. */
  amountDelta?: number;
  /** Days the close date moved (positive = slipped later). */
  slipDays?: number;
}

export interface DealMovementBucket {
  count: number;
  amount: number;
  deals: DealMovementItem[];
}

/**
 * Response of GET /deals/pipeline-movement — what changed since a baseline
 * date, computed by diffing daily DealSnapshots against live deals.
 */
export interface DealPipelineMovement {
  since: string;
  asOf: string;
  /** False when no snapshot history exists at/before `since` yet. */
  hasBaseline: boolean;
  created: DealMovementBucket;
  won: DealMovementBucket;
  lost: DealMovementBucket;
  /** Open deals whose closeDate moved later than the baseline. */
  slipped: DealMovementBucket;
  /** Open deals whose closeDate moved earlier. */
  pulledIn: DealMovementBucket;
  /** Open deals whose amount grew (bucket.amount = total positive delta). */
  increased: DealMovementBucket;
  /** Open deals whose amount shrank (bucket.amount = total negative delta, as absolute). */
  decreased: DealMovementBucket;
  /** Open deals whose canonical stage moved backward. */
  regressed: DealMovementBucket;
}

// Deal schema
export const DealSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  entityId: z.string().uuid().optional().nullable(), // Client/Entity relationship
  primaryContactId: z.string().uuid().optional().nullable(), // Path C: stakeholder
  memberId: z.string().uuid().optional(),
  assignedToId: z.string().uuid().optional(),
  amount: decimalSchema,
  probability: z.number().nullable().optional(), // Rep/manual confidence — can override stage probability
  // T6: derived win-probability (tenant's historical win-rate for this deal's
  // canonical stage). Server-computed, stored separately from `probability`.
  derivedProbability: z.number().nullable().optional(),
  derivedProbabilitySampleSize: z.number().nullable().optional(), // N behind derivedProbability (T9)
  derivedProbabilityCalculatedAt: z.date().nullable().optional(),
  // T7: rep confidence − derived probability. Server-maintained (null unless
  // both inputs exist); stored so it is directly sortable/filterable.
  derivedProbabilityDelta: z.number().nullable().optional(),
  maxStageOrdinal: z.number().nullable().optional(), // Furthest canonical stage reached (T6 internal)
  healthScore: z.number().nullable().optional(),
  healthLevel: z.enum(['strong', 'watch', 'risk']).nullable().optional(),
  healthReasons: z.array(z.string()).optional(),
  healthCalculatedAt: z.date().nullable().optional(),
  closeDate: z.date(),
  // Expected work-start anchor for resource planning. When set, demand seeding
  // (T14) anchors role-hour windows here; otherwise closeDate + template lag.
  expectedStartDate: z.date().nullable().optional(),
  stage: z.string().default('prospecting'), // References CrmStage.stageId
  stageTypeId: z.nativeEnum(CrmStageTypeEnum).nullable().optional(), // Denormalized for filtering
  // Forecast call (Clari-style). DB defaults to 'pipeline'; optional in the
  // type so construction sites aren't all forced to set it.
  forecastCategory: DealForecastCategorySchema.optional(),
  // T11 — deal-shape metadata. Lets demand be seeded from a deal (T14): the
  // deal type carries a typical deliverable mix, each row a rough size the
  // T12 templates later expand into role-hours.
  dealType: DealTypeSchema.nullable().optional(),
  // Optional in the type (not `.default([])`) so every Deal-construction site
  // isn't forced to set it; the DB column defaults to `[]`, so a persisted deal
  // always reads back an array.
  deliverableMix: DealDeliverableMixSchema.optional(),
  // T2/T3 — record lifecycle (orthogonal to the funnel stage). Server-managed:
  // 'merged' set by the merge engine (T3); mergedIntoId/mergedAt let a
  // tombstone render its "Merged into X" redirect banner (U2).
  recordState: RecordStateSchema.optional(),
  mergedIntoId: z.string().uuid().nullable().optional(),
  mergedAt: z.date().nullable().optional(),
  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion; null for records created before T1 or outside a wired producer.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
  metadata: z.record(z.string(), z.any()).optional(), // e.g. { source: 'migrated_from_lead' }
  // Won-deal conversion provenance: contracts created from this deal
  // (Contract.dealId back-ref, take-1 in dealSelect). Non-empty = converted.
  contracts: z
    .array(z.object({ id: z.string().uuid(), title: z.string() }))
    .optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date(),
  customFields: z.record(z.string(), z.any()).optional(),
  // Populated relations
  entity: z.any().optional(), // Populated Entity object
  primaryContact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    entityId: z.string().uuid().nullable().optional(),
  }).optional().nullable(), // Populated Contact object
});

export type Deal = z.infer<typeof DealSchema>;

// Create Deal schema
export const CreateDealSchema = DealSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  memberId: true,
  assignedToId: true,
  derivedProbability: true, // Server-computed (T6) — never client-set
  derivedProbabilitySampleSize: true,
  derivedProbabilityCalculatedAt: true,
  derivedProbabilityDelta: true, // Server-derived (T7) — never client-set
  maxStageOrdinal: true,
  recordState: true, // Server-managed lifecycle (T2/T3) — never client-set
  mergedIntoId: true,
  mergedAt: true,
  captureId: true, // Server-managed provenance (T1) — never client-set
  capture: true,
  contracts: true, // Server-populated conversion back-ref — never client-set
  healthScore: true,
  healthLevel: true,
  healthReasons: true,
  healthCalculatedAt: true,
  entity: true, // Don't include populated relation in create
  primaryContact: true, // Don't include populated relation in create
}).extend({
  address: z.any().optional(),
  entityId: z.string().uuid().optional().nullable(), // Allow entityId on create
  primaryContactId: z.string().uuid().optional().nullable(), // Allow primary contact on create
  customFields: z.record(z.string(), z.any()).optional(),
});

export type CreateDeal = z.infer<typeof CreateDealSchema>;

// Update Deal schema
export const UpdateDealSchema = CreateDealSchema.partial().extend({
  customFields: z.record(z.string(), z.any()).optional(),
});

export type UpdateDeal = z.infer<typeof UpdateDealSchema>;

// Deal response schema
export const DealResponseSchema = z.object({
  data: DealSchema,
  message: z.string().optional(),
});

export type DealResponse = z.infer<typeof DealResponseSchema>;

// Deals response schema
export const DealsResponseSchema = z.object({
  data: z.array(DealSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type DealsResponse = z.infer<typeof DealsResponseSchema>;
